import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpDown, Users, RefreshCw, FileText, History } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FiltersBar } from "@/components/FiltersBar";
import { api, errMsg, paramsFrom } from "@/lib/api";
import { AREAS, total, ageFrom, fmtDate, fmtScore, band } from "@/lib/heat";

const COLS = [
  { key: "name", label: "Name" },
  { key: "organization", label: "Church / Org" },
  { key: "event_group", label: "Event / Client Group" },
  { key: "place", label: "City, State" },
  { key: "gender", label: "Gender" },
  { key: "race", label: "Race / Ethnicity" },
  { key: "age", label: "Age" },
  { key: "submitted_at", label: "Submitted" },
  ...AREAS.map((a) => ({ key: a.key, label: a.label, num: true })),
  { key: "total", label: "Total", num: true },
];

const valueOf = (r, k) => {
  if (k === "place") return [r.city, r.state].filter(Boolean).join(", ").toLowerCase();
  if (k === "age") return ageFrom(r.dob) ?? -1;
  if (k === "total") return total(r.scores);
  if (AREAS.some((a) => a.key === k)) return r.scores[k];
  return (r[k] || "").toString().toLowerCase();
};

export default function Entries() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "submitted_at", dir: -1 });
  const [syncing, setSyncing] = useState(false);
  const nav = useNavigate();
  const qs = searchParams.toString();

  const load = () => {
    setLoading(true);
    api.get("/submissions", { params: paramsFrom(new URLSearchParams(qs)) })
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs]);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const x = valueOf(a, sort.key);
      const y = valueOf(b, sort.key);
      return (x > y ? 1 : x < y ? -1 : 0) * sort.dir;
    });
  }, [rows, sort]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/ghl/sync");
      toast.success(`Synced: ${data.new_submissions} new, ${data.report_links_sent} report links sent`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Assessment entries</div>
          <h1 className="mt-1 text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1F4266]">Entries</h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="sync-now-button" variant="outline" className="rounded-full" onClick={sync} disabled={syncing}>
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> Sync from GHL
          </Button>
          <Button data-testid="build-group-report-button" className="rounded-full bg-[#1F4266] hover:bg-[#122A43]" onClick={() => { const p = new URLSearchParams(qs); p.delete("history"); nav(`/reports/group?${p}`); }}>
            <Users className="mr-2 h-4 w-4" /> Group report
          </Button>
        </div>
      </div>
      <FiltersBar searchParams={searchParams} setSearchParams={setSearchParams} showHistoryToggle />
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm fade-up-2">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="entries-table">
            <thead className="bg-slate-50 text-left">
              <tr>
                {COLS.map((c) => (
                  <th key={c.key} className={`px-3 py-3 font-semibold text-slate-600 whitespace-nowrap ${c.num ? "text-right" : ""}`}>
                    <button
                      data-testid={`sort-${c.key}`}
                      className="inline-flex items-center gap-1 hover:text-[#1F4266] transition-colors"
                      onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : 1 }))}
                    >
                      {c.label} <ArrowUpDown className={`h-3 w-3 ${sort.key === c.key ? "text-[#1F4266]" : "opacity-40"}`} />
                    </button>
                  </th>
                ))}
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} data-testid={`entry-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors">
                  <td className="px-3 py-3 font-medium text-slate-900 whitespace-nowrap">
                    {r.name} {r.is_sample && <Badge variant="secondary" className="ml-1 text-[10px]">Sample</Badge>}
                    {r.entry_type === "group" && <Badge data-testid={`group-entry-badge-${r.id}`} className="ml-1 bg-[#1F4266] text-[10px]">Group/Team</Badge>}
                  </td>
                  <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{r.organization || "—"}</td>
                  <td className="px-3 py-3 text-slate-600 max-w-[200px] truncate" title={r.event_group || ""}>{r.event_group || "—"}</td>
                  <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{[r.city, r.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-3 py-3 text-slate-600">{r.gender || "—"}</td>
                  <td className="px-3 py-3 text-slate-600 max-w-[180px] truncate" title={r.race || ""}>{r.race || "—"}</td>
                  <td className="px-3 py-3 text-slate-600 font-mono">{ageFrom(r.dob) ?? "—"}</td>
                  <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{fmtDate(r.submitted_at)}</td>
                  {AREAS.map((a) => (
                    <td key={a.key} className="px-3 py-3 text-right font-mono" style={{ color: band(r.scores[a.key]).hex }}>
                      {fmtScore(r.scores[a.key])}
                    </td>
                  ))}
                  <td className="px-3 py-3 text-right font-mono font-semibold text-slate-900">{fmtScore(total(r.scores))}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <Link data-testid={`view-history-${r.id}`} to={`/reports/person/${encodeURIComponent(r.person_key || r.id)}`} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100 font-medium">
                      <History className="h-3.5 w-3.5" /> {r.entry_count > 1 ? `${r.entry_count} results` : "History"}
                    </Link>
                    <Link data-testid={`view-report-${r.id}`} to={`/reports/individual/${r.id}`} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[#1F4266] hover:bg-slate-100 font-medium whitespace-nowrap">
                      <FileText className="h-3.5 w-3.5" /> Report
                    </Link>
                  </td>
                </tr>
              ))}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={COLS.length + 1} className="px-4 py-16 text-center text-slate-500" data-testid="entries-empty">
                    No entries match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100 px-3 py-3 text-xs text-slate-500 font-mono" data-testid="entries-count">
          {loading ? "Loading…" : `${rows.length} entr${rows.length === 1 ? "y" : "ies"}`}
        </div>
      </div>
    </div>
  );
}
